import { difference, findLastIndex, flatMap, flatten } from 'lodash';
import { assert, isOfType, unreachable } from '../../../../ts-utils';
export const addCommentChunkToEnd = ({ chunks, newChunk, pageLimit, liveCommentsPurgatory, }) => {
    const pageCount = chunks.filter(isOfType('FETCHED')).length;
    if (pageCount + 1 <= pageLimit) {
        return { chunks: [...chunks, newChunk], commentIdsToRemove: [] };
    }
    // as the chunks added by one, dropping one chunk should be enough
    const firstFetchedChunkIndex = chunks.findIndex(isOfType('FETCHED'));
    const removedChunk = chunks[firstFetchedChunkIndex];
    assert(isOfType('FETCHED', removedChunk));
    return {
        chunks: [...chunks.filter((_v, index) => index !== firstFetchedChunkIndex), newChunk],
        commentIdsToRemove: getCommentsToRemove(removedChunk, chunks, liveCommentsPurgatory),
    };
};
export const addComentChunkToStart = ({ chunks, newChunk, pageLimit, liveCommentsPurgatory, }) => {
    const pageCount = chunks.filter(isOfType('FETCHED')).length;
    if (pageCount + 1 <= pageLimit) {
        return { chunks: [newChunk, ...chunks], commentIdsToRemove: [] };
    }
    const lastFetchedChunkIndex = findLastIndex(chunks, isOfType('FETCHED'));
    const removedChunk = chunks[lastFetchedChunkIndex];
    assert(isOfType('FETCHED', removedChunk));
    return {
        chunks: [newChunk, ...chunks.filter((_v, index) => index !== lastFetchedChunkIndex)],
        commentIdsToRemove: getCommentsToRemove(removedChunk, chunks, liveCommentsPurgatory),
    };
};
export const addRepliesChunkToEnd = ({ chunks, newChunk, newReplies, pageLimit, liveRepliesPurgatory, }) => {
    const pageCount = chunks.filter(isOfType(['INITIAL', 'FETCHED'])).length;
    if (pageCount + 1 <= pageLimit) {
        return { chunks: [...chunks, newChunk], replyIdsToRemove: [] };
    }
    // as the chunks added by one, dropping one chunk should be enough
    const removedChunkIndex = chunks.findIndex(isOfType(['FETCHED', 'INITIAL']));
    const removedChunk = chunks[removedChunkIndex];
    assert(isOfType(['FETCHED', 'INITIAL'], removedChunk));
    return {
        chunks: [...chunks.filter((_v, index) => index !== removedChunkIndex), newChunk],
        replyIdsToRemove: getRepliesToRemove({
            removedChunk,
            newReplies,
            chunks,
            liveRepliesPurgatory,
        }),
    };
};
export const addRepliesChunkToStart = ({ chunks, newChunk, newReplies, pageLimit, liveRepliesPurgatory, }) => {
    const pageCount = chunks.filter(isOfType(['INITIAL', 'FETCHED'])).length;
    if (pageCount + 1 <= pageLimit) {
        return { chunks: [newChunk, ...chunks], replyIdsToRemove: [] };
    }
    // as the chunks added by one, dropping one chunk should be enough
    const removedChunkIndex = findLastIndex(chunks, isOfType(['FETCHED', 'INITIAL']));
    const removedChunk = chunks[removedChunkIndex];
    assert(isOfType(['FETCHED', 'INITIAL'], removedChunk));
    return {
        chunks: [newChunk, ...chunks.filter((_v, index) => index !== removedChunkIndex)],
        replyIdsToRemove: getRepliesToRemove({
            removedChunk,
            newReplies,
            chunks,
            liveRepliesPurgatory,
        }),
    };
};
const getCommentsToRemove = (removedChunk, chunks, liveCommentsPurgatory) => {
    // Newly created comments might appear in loaded chunks, and we can not drop them
    const commentIdsCreatedInThisSession = flatten(chunks
        .map((chunk) => {
        switch (chunk.type) {
            case 'NEW':
                return [chunk.localOrCommentId];
            case 'LIVE':
                return chunk.commentIds;
            case 'FETCHED':
                return [];
            default:
                throw unreachable(chunk);
        }
    })
        .concat(liveCommentsPurgatory));
    return difference(removedChunk.commentIds, commentIdsCreatedInThisSession);
};
const getRepliesToRemove = ({ removedChunk, newReplies, chunks, liveRepliesPurgatory, }) => {
    const getChildren = (commentId) => flatMap(newReplies[commentId] || [], (childId) => [childId, ...getChildren(childId)]);
    // New replies to other replies should be dropped together with parent reply
    const newRepliesToReplies = flatMap(removedChunk?.commentIds || [], getChildren);
    // Replies to comment should stay, so we should not drop them even if they are appearing in fetched chunk
    const newRepliesToComment = chunks.filter(isOfType(['NEW'])).map((c) => c.localOrCommentId);
    // The same goes with target chunks
    const repliesInTargetChunks = chunks.filter(isOfType(['REPLY_TARGET'])).map((c) => c.commentId);
    // And live comments
    const liveReplies = flatten(chunks.filter(isOfType(['LIVE'])).map((c) => c.commentIds)).concat(liveRepliesPurgatory);
    return [
        ...difference(removedChunk?.commentIds || [], newRepliesToComment, repliesInTargetChunks, liveReplies),
        ...newRepliesToReplies,
    ];
};
//# sourceMappingURL=comment-drop-utils.js.map